import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSubprocesses, SubprocessOption } from "../select/SubprocessCells";
import { useProcessDefinitions, ProcessDefOption } from "../select/ProcessCells";

interface TreeStage {
  _id: string;
  name: string;
  order?: number;
  // First entry of the subprocess codebook's own `statuses[]`, display-only — see
  // `showSubprocessStatus` prop below. Never a per-work-order value.
  statusLabel?: string;
}

interface TreeGroup {
  key: string;
  label: string;
  sortName: string;
  order?: number;
  stages: TreeStage[];
  // true when this "group" is really a single subprocess-less process definition
  // (e.g. Размерување) — rendered as one clickable step, not a collapsible header.
  isLeaf?: boolean;
}

const resolveProcessDefinition = (
  processDefinition: SubprocessOption["processDefinition"]
): { id: string | null; code?: string; name?: string; order?: number } => {
  if (!processDefinition) return { id: null };
  if (typeof processDefinition === "string") return { id: processDefinition };
  return { id: processDefinition._id || null, code: processDefinition.code, name: processDefinition.name, order: processDefinition.order };
};

const buildGroupLabel = (code?: string, name?: string): string => {
  // Show only the process-definition NAME in the OS nav tree (code intentionally omitted).
  return name || code || "";
}

// Sort key shared by the group list and the leaf-step list: 0/undefined = "unordered"
// -> pushed last (set via drag on /process-definitions).
const effectiveOrder = (o?: number) => (typeof o === "number" && o > 0 ? o : Number.MAX_SAFE_INTEGER);

// Client-only sentinel for the "Размерување" (weighing) stage. Weighing is a top-level
// PROCESS DEFINITION with no Subprocess documents under it, so it can never be a real
// Subprocess `_id`. Its existence, label and position in the tree are resolved from the
// process-definitions codebook (see useRazmerProcessDefinition below) — never hardcoded
// and never guessed from its name. NEVER persist this string into `razmer[].subprocess` —
// that field is a Mongoose ref('Subprocess') and the backend `.populate()`s it; save
// `subprocess: null` instead.
export const RAZMER_STAGE_ID = "__razmer__";

/** The weighing process: the active process definition that has no subprocesses.
 *  Identity comes from the codebook, never from its name. When more than one
 *  definition has zero subprocesses, only the first by `order` counts as
 *  "weighing" — the rest have no way to be addressed (there is only one
 *  sentinel) and are ignored here, matching the tree-building logic below. */
export const useRazmerProcessDefinition = (): ProcessDefOption | undefined => {
  const processDefinitions = useProcessDefinitions();
  const subprocesses = useSubprocesses();
  return useMemo(() => {
    const withSubprocesses = new Set<string>();
    subprocesses.forEach((sp) => {
      const { id } = resolveProcessDefinition(sp.processDefinition);
      if (id) withSubprocesses.add(id);
    });
    const leafDefs = processDefinitions
      .filter((pd) => !withSubprocesses.has(pd._id))
      .sort((a, b) => effectiveOrder(a.order) - effectiveOrder(b.order));
    return leafDefs[0];
  }, [processDefinitions, subprocesses]);
};

interface Props {
  activeStage: string;
  onSelect: (id: string) => void;
  // When provided, only these subprocess ids are shown (the current ident's
  // selected subprocesses). Undefined = show all (codebook browsing).
  filterSubprocessIds?: string[];
  // /work-order only: show each subprocess's status (first entry of its own
  // `statuses[]`, read straight from the subprocess codebook) in parentheses
  // after its name. Display-only — there is no per-work-order status field.
  // /os passes nothing and stays visually unchanged.
  showSubprocessStatus?: boolean;
  /** RN-only transient status for the synthetic Razmer leaf. */
  razmerStatusLabel?: string;
  /** "stacked": processes one under another (narrow side column on /os). Default: one column per process. */
  layout?: "row" | "stacked";
  /** RN only: subprocesses the user may see (HR assignment). Undefined = all. */
  allowedSubprocessIds?: Set<string>;
  /** RN only: small warning shown under the tree. */
  notice?: string;
}

const OSStageTree: React.FC<Props> = ({ activeStage, onSelect, filterSubprocessIds, showSubprocessStatus = false, razmerStatusLabel, layout = "row", allowedSubprocessIds, notice }) => {
  const stacked = layout === "stacked";
  const { t } = useTranslation();
  const subprocesses = useSubprocesses();
  const processDefinitions = useProcessDefinitions();

  const groups: TreeGroup[] = useMemo(() => {
    const byProcessDefinition = new Map<string, { name?: string; code?: string; order?: number; stages: TreeStage[] }>();
    const fallbackStages: TreeStage[] = [];
    const filterSet = filterSubprocessIds ? new Set(filterSubprocessIds) : null;

    // Leaf-ness is judged on the FULL subprocess codebook, never on the filtered view:
    // on /work-order the tree is narrowed to this ident's selected subprocesses, so a
    // process whose subprocesses are all filtered out would otherwise look
    // subprocess-less and render as a weighing step. Same rule as
    // useRazmerProcessDefinition above — the two must agree.
    const definitionsWithSubprocesses = new Set<string>();
    subprocesses.forEach((sp) => {
      const { id } = resolveProcessDefinition(sp.processDefinition);
      if (id) definitionsWithSubprocesses.add(id);
    });

    subprocesses.forEach((sp) => {
      if (filterSet && !filterSet.has(sp._id)) return;
      if (allowedSubprocessIds && !allowedSubprocessIds.has(sp._id)) return;
      const statusLabel = showSubprocessStatus ? sp.statuses?.[0]?.name || undefined : undefined;
      const stage: TreeStage = { _id: sp._id, name: sp.name || "", order: sp.order, statusLabel };
      const { id, code, name, order } = resolveProcessDefinition(sp.processDefinition);
      if (!id) {
        fallbackStages.push(stage);
        return;
      }
      const existing = byProcessDefinition.get(id);
      if (existing) {
        existing.stages.push(stage);
        if (!existing.code && code) existing.code = code;
        if (!existing.name && name) existing.name = name;
        if (existing.order == null && order != null) existing.order = order;
      } else {
        byProcessDefinition.set(id, { name, code, order, stages: [stage] });
      }
    });

    const sortStages = (stages: TreeStage[]) =>
      [...stages].sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        const nameCmp = (a.name || "").localeCompare(b.name || "", "mk");
        if (nameCmp !== 0) return nameCmp;
        return a._id.localeCompare(b._id);
      });

    const realGroups: TreeGroup[] = Array.from(byProcessDefinition.entries()).map(([id, g]) => ({
      key: id,
      label: buildGroupLabel(g.code, g.name),
      sortName: g.name || "",
      order: g.order,
      stages: sortStages(g.stages),
    }));

    // Leaf steps: active process definitions with NO subprocesses (e.g. Размерување).
    // Rendered as a single clickable step rather than a collapsible group. Only the
    // FIRST such definition (by order) can ever be selected — its onSelect emits the
    // RAZMER_STAGE_ID sentinel, the only one that exists — so any further
    // subprocess-less definition is silently skipped (kept in sync with
    // useRazmerProcessDefinition above).
    const leafDefs = processDefinitions
      .filter((pd) => !definitionsWithSubprocesses.has(pd._id))
      .sort((a, b) => {
        const oa = effectiveOrder(a.order);
        const ob = effectiveOrder(b.order);
        if (oa !== ob) return oa - ob;
        return (a.name || "").localeCompare(b.name || "", "mk");
      });
    const leafGroups: TreeGroup[] = leafDefs.slice(0, 1).map((pd) => ({
      key: pd._id,
      label: pd.name || pd.code || "",
      sortName: pd.name || "",
      order: pd.order,
      stages: [],
      isLeaf: true,
    }));

    // Sort GROUPS (including the leaf step) by the process-definition `order` (set via
    // drag on /process-definitions); 0/undefined = "unordered" -> pushed last, then
    // alphabetical by name.
    const combined = [...realGroups, ...leafGroups];
    combined.sort((a, b) => {
      const oa = effectiveOrder(a.order);
      const ob = effectiveOrder(b.order);
      if (oa !== ob) return oa - ob;
      return (a.sortName || a.label || "").localeCompare(b.sortName || b.label || "", "mk");
    });

    const result = [...combined];
    if (fallbackStages.length > 0) {
      result.push({
        key: "__other__",
        label: t("os.stageTree.other", "Останато"),
        sortName: "",
        stages: sortStages(fallbackStages),
      });
    }
    return result;
  }, [subprocesses, processDefinitions, t, filterSubprocessIds, showSubprocessStatus, allowedSubprocessIds]);

  useEffect(() => {
    if (activeStage) return;
    // Default to the weighing leaf when the codebook has one; otherwise fall back to
    // the first stage of the first non-empty group. Never select a step the tree
    // doesn't contain.
    const razmerGroup = groups.find((g) => g.isLeaf);
    if (razmerGroup) {
      onSelect(RAZMER_STAGE_ID);
      return;
    }
    const firstGroupWithStages = groups.find((g) => g.stages.length > 0);
    if (firstGroupWithStages) {
      onSelect(firstGroupWithStages.stages[0]._id);
    }
  }, [activeStage, groups, onSelect]);

  // Active PROCESS is derived from activeStage, never tracked as separate state: the
  // leaf (Размерување) maps to itself via the RAZMER_STAGE_ID sentinel, any other
  // stage maps to whichever group's `stages` contains it.
  const activeGroup = useMemo(() => {
    if (activeStage === RAZMER_STAGE_ID) return groups.find((g) => g.isLeaf);
    return groups.find((g) => !g.isLeaf && g.stages.some((s) => s._id === activeStage));
  }, [groups, activeStage]);

  const renderStage = (stage: TreeStage, withSeparator: boolean) => {
    const isActive = activeStage === stage._id;
    const status = (isActive && razmerStatusLabel) || stage.statusLabel;
    return (
      <React.Fragment key={stage._id}>
        {withSeparator && <span className="text-gray-300 text-xs">›</span>}
        <button
          type="button"
          onClick={() => onSelect(stage._id)}
          title={stacked ? [stage.name, status].filter(Boolean).join(" — ") : undefined}
          className={`rounded-full font-semibold whitespace-nowrap transition-all ${stacked ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"} ${
            isActive
              ? "bg-blue-50 text-blue-700 ring-2 ring-blue-500/60"
              : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-blue-50 hover:text-blue-700"
          }`}
        >
          {stage.name}
          {/* Side column: status only in the tooltip, so the column stays narrow. */}
          {status && !stacked ? ` (${isActive && razmerStatusLabel ? razmerStatusLabel : stage.statusLabel})` : ""}
        </button>
      </React.Fragment>
    );
  };

  return (
    <nav className="space-y-2">
      {groups.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-4 text-center text-xs text-gray-400 shadow-md shadow-gray-300/40">
          {t("os.stageTree.empty", "Нема дефинирани потпроцеси")}
        </div>
      )}
      {groups.length > 0 && (
        // Process stepper: one column per process (numbered, in production order), its
        // subprocesses as pills underneath. Columns share the full width.
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-md shadow-gray-300/40">
          <div className={stacked ? "flex flex-col w-full" : "flex min-w-max w-full"}>
            {groups.map((group, gi) => {
              const dividerClass = gi === 0 ? "" : stacked ? "border-t border-gray-200" : "border-l border-gray-200";
              const isActiveGroup = group.isLeaf
                ? activeStage === RAZMER_STAGE_ID
                : activeGroup?.key === group.key;
              const headerLabel = group.isLeaf && razmerStatusLabel && !stacked
                ? `${group.label} (${razmerStatusLabel})`
                : group.label;

              return (
                <div key={group.key} className={`flex flex-1 flex-col${stacked ? " min-w-0" : ""} ${dividerClass}`}>
                  <button
                    type="button"
                    onClick={() => onSelect(group.isLeaf ? RAZMER_STAGE_ID : group.stages[0]._id)}
                    className={`flex w-full items-center gap-2 py-2 font-semibold transition-all ${stacked ? "justify-start px-2 text-xs" : "justify-center px-3 text-sm"} ${
                      isActiveGroup
                        ? "themed-tab-active text-white"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                  >
                    <span
                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
                        isActiveGroup ? "bg-white/25 text-white" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {gi + 1}
                    </span>
                    {headerLabel}
                  </button>
                  {!stacked && (
                    <div className="flex flex-wrap items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px]">
                      {!group.isLeaf && group.stages.map((stage, si) => renderStage(stage, si > 0))}
                    </div>
                  )}
                  {stacked && !group.isLeaf && (
                    <div className="flex flex-wrap gap-1 px-1.5 py-1.5">
                      {group.stages.map((stage) => renderStage(stage, false))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {notice && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {notice}
        </div>
      )}
    </nav>
  );
};

export default OSStageTree;
